/**
 * @fileoverview Hook for public IT handoff validation and submission.
 * @module system-web/hooks/useSsoHandoff
 *
 * Responsibilities:
 * - Load one-time handoff status and expose separate load and submit errors.
 * - Submit credentials while tracking pending and terminal-link states.
 *
 * Dependencies:
 * - Public handoff service functions and React state/effect hooks.
 *
 * Architecture:
 * - Owns transient UI workflow state for an inherently online public handoff.
 *
 * Notes:
 * - Recoverable submit failures remain distinct from link-verification failures.
 *
 * @hook
 * Purpose: Manages the public handoff request lifecycle.
 * Returns: Handoff status, loading/submission state, errors, and submit action.
 * Side Effects: Loads status and submits credentials through the handoff service.
 * Offline Behavior: Unavailable offline; submission requires the server.
 * @security Does not expose route tokens through logs or telemetry.
 */
import { useCallback, useEffect, useState } from 'react';
import { getSsoHandoff, submitSsoHandoff, type SsoHandoffStatus } from '../services/ssoHandoffService';

export function useSsoHandoff(token: string) {
  const [status, setStatus] = useState<SsoHandoffStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadErrorCode, setLoadErrorCode] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorCode, setSubmitErrorCode] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(null);
    setLoadErrorCode(null);
    setSubmitError(null);
    setSubmitErrorCode(null);
    if (!token) {
      setLoadError('This handoff link is invalid.');
      setLoadErrorCode('HANDOFF_NOT_FOUND');
      setLoading(false);
      return () => { active = false; };
    }
    getSsoHandoff(token).then((value) => {
      if (active) setStatus(value);
    }).catch((value) => {
      if (active) {
        setLoadError(value instanceof Error ? value.message : 'This handoff link is unavailable.');
        setLoadErrorCode(value?.apiBody?.code ?? null);
      }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const submit = useCallback(async (credentials: {
    clientId: string;
    clientSecret: string;
    upstreamTenantId: string;
    onMicrosoftDomain: string;
    email: string;
    secretExpiresAt: string;
    verifiedEmailDomains: string[];
    itConfirmations: Record<string, boolean>;
  }) => {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitErrorCode(null);
    try {
      const result = await submitSsoHandoff(token, credentials);
      setStatus((current) => ({ ...current, setupStatus: result.setupStatus }));
      return result;
    } catch (value) {
      const message = value instanceof Error ? value.message : 'Unable to submit credentials.';
      const code = (value as any)?.apiBody?.code ?? null;
      if (['HANDOFF_NOT_FOUND', 'HANDOFF_CONSUMED', 'HANDOFF_UNAVAILABLE', 'HANDOFF_EXPIRED'].includes(code)) {
        setLoadError(message);
        setLoadErrorCode(code);
      } else {
        setSubmitError(message);
        setSubmitErrorCode(code);
      }
      throw value;
    } finally {
      setSubmitting(false);
    }
  }, [token]);

  return { status, loading, submitting, loadError, loadErrorCode, submitError, submitErrorCode, submit };
}
